import React from "react";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { AssetIcon } from "components/common/AssetIcon";
import { InlineSpinner } from "components/common/InlineSpinner";
import type { LeaveTeamPreview } from "./TeamsApi";

/// The leave-team preview lifecycle, shared by the confirm dialog and the panel.
export type LeavePreviewState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "loaded"; data: LeaveTeamPreview }
  | { status: "error"; reason: string };

export const formatImpactSentence = (data: LeaveTeamPreview): string => {
  const { tables_affected, models_affected } = data;
  const tableWord = tables_affected === 1 ? "table" : "tables";
  // Always plural ("1 of your models") — "1 of your model" reads wrong.
  return `${tables_affected} ${tableWord} in ${models_affected} of your models will stop syncing.`;
};

interface LeaveTeamImpactPanelProps {
  preview: LeavePreviewState;
}

/// Renders which tables/models stop syncing if the caller leaves the team.
export const LeaveTeamImpactPanel: React.FC<LeaveTeamImpactPanelProps> = ({
  preview,
}) => {
  if (preview.status === "idle") return null;

  if (preview.status === "loading") {
    return (
      <div
        className="mt-3 flex items-center gap-2 text-fg-muted-regular"
        data-testid="leave-team-preview-loading"
      >
        <InlineSpinner size={14} />
        <span>Checking impact…</span>
      </div>
    );
  }

  if (preview.status === "error") {
    return (
      <div
        className="mt-3 flex items-start gap-2 text-fg-muted-regular"
        data-testid="leave-team-preview-error"
      >
        <WarningAmberRoundedIcon
          className="shrink-0"
          sx={{ color: "var(--color-warning)", fontSize: 16, marginTop: "1px" }}
          aria-hidden="true"
        />
        <span>
          We can&apos;t check which tables will stop syncing right now. If you
          leave, tables from this team may be affected.
        </span>
      </div>
    );
  }

  const { tables_affected, per_model } = preview.data;
  if (tables_affected === 0) {
    return (
      <div
        className="mt-3 flex items-center gap-1 text-fg-muted-regular"
        data-testid="leave-team-preview-zero"
      >
        <CheckRoundedIcon
          sx={{ color: "var(--color-fg-muted)", fontSize: 14 }}
          aria-hidden="true"
        />
        <span>None of your tables will lose their source.</span>
      </div>
    );
  }

  return (
    <ul
      className="mt-3 border-t"
      style={{ borderColor: "var(--color-border)" }}
      data-testid="leave-team-preview-impact"
    >
      {per_model.map((m) => (
        <li
          key={m.model_id}
          className="grid gap-2 border-b py-2 last:border-b-0"
          style={{
            borderColor: "var(--color-border)",
            gridTemplateColumns: "20px 1fr",
          }}
        >
          <AssetIcon
            size="sm"
            color={m.model_color ?? undefined}
            iconIndex={m.model_icon_index ?? undefined}
          />
          <div>
            <div className="text-fg-medium">{m.model_name}</div>
            <ul className="mt-1 space-y-0.5">
              {m.tables.map((t) => (
                <li
                  key={t.table_id}
                  className="text-fg-muted-regular pl-2 relative before:content-[''] before:absolute before:left-0 before:top-2.25 before:w-1 before:h-px before:bg-current before:opacity-60"
                >
                  {t.table_name}
                </li>
              ))}
            </ul>
          </div>
        </li>
      ))}
    </ul>
  );
};
